/**
 * stage-fields-dialog.tsx — Hộp thoại điền "Trường bổ sung" khi đưa khách vào một giai đoạn (kiểu bước của luồng Lark).
 * Mở bằng `useStageMove().move(...)` (hooks/use-stage-move.ts) khi giai đoạn đích có khai trường; gắn một lần ở app-layout.
 * Giá trị có sẵn của khách được điền sẵn; trường bắt buộc còn trống thì chưa cho chuyển.
 */
import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { builtinField, isPropRef, propKeyOf } from '@/lib/stage-fields'
import { useChangeLifecycleStage, useContactProperties, type ContactPropertyValue } from '@/hooks/use-cdp'
import { useContact } from '@/hooks/use-contacts'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useStageChangeStore, type StageChangeRequest } from '@/stores/stage-change-store'

interface FieldView {
  ref: string
  label: string
  required: boolean
  type: 'text' | 'longtext' | 'date' | 'number' | 'boolean' | 'select'
  options?: { value: string; label: string }[]
}

export function StageFieldsDialog() {
  const request = useStageChangeStore((s) => s.request)
  const close = useStageChangeStore((s) => s.close)
  return (
    <Dialog open={!!request} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">{request && <StageFieldsForm request={request} onClose={close} />}</DialogContent>
    </Dialog>
  )
}

function StageFieldsForm({ request, onClose }: { request: StageChangeRequest; onClose: () => void }) {
  const { t } = useTranslation('sharedMisc')
  const { stages } = useLifecycleStages()
  const stage = stages.find((s) => s.key === request.toStage)
  const contact = useContact(request.contactId)
  const props = useContactProperties(request.contactId)
  const change = useChangeLifecycleStage()
  const [values, setValues] = useState<Record<string, string>>({})
  const [ready, setReady] = useState(false)

  // Dựng danh sách trường: trường hệ thống đọc từ khách, thuộc tính tuỳ biến đọc từ định nghĩa + giá trị của khách.
  const propByKey = useMemo(() => new Map((props.data?.properties ?? []).map((p) => [p.fieldKey, p] as const)), [props.data])
  const fields: FieldView[] = useMemo(() => {
    const out: FieldView[] = []
    for (const f of stage?.fields ?? []) {
      if (isPropRef(f.ref)) {
        const p: ContactPropertyValue | undefined = propByKey.get(propKeyOf(f.ref))
        if (!p) continue // thuộc tính đã bị xoá
        const type: FieldView['type'] =
          p.fieldType === 'number' ? 'number' : p.fieldType === 'date' ? 'date' : p.fieldType === 'boolean' ? 'boolean' : p.fieldType === 'single_select' ? 'select' : 'text'
        out.push({ ref: f.ref, label: p.name, required: f.required, type, options: (p.options ?? []).map((o) => ({ value: o.value, label: o.label })) })
      } else {
        const b = builtinField(f.ref)
        if (b) out.push({ ref: f.ref, label: b.label, required: f.required, type: b.type })
      }
    }
    return out
    // `b.label` (BUILTIN_STAGE_FIELDS) là getter theo ngôn ngữ — cần `t` trong deps để tính lại khi đổi ngôn ngữ.
  }, [stage, propByKey, t])

  // Điền sẵn một lần khi đã có dữ liệu khách.
  useEffect(() => {
    if (ready || !contact.data || !props.data) return
    const c = contact.data
    const init: Record<string, string> = {}
    for (const f of fields) {
      if (isPropRef(f.ref)) init[f.ref] = propByKey.get(propKeyOf(f.ref))?.value ?? ''
      else if (f.ref === 'birthday') init[f.ref] = c.birthday ? String(c.birthday).slice(0, 10) : ''
      else init[f.ref] = String((c as unknown as Record<string, unknown>)[f.ref] ?? '')
    }
    setValues(init)
    setReady(true)
  }, [ready, contact.data, props.data, fields, propByKey])

  const set = (ref: string, v: string) => setValues((x) => ({ ...x, [ref]: v }))
  const missing = fields.filter((f) => f.required && !(values[f.ref] ?? '').trim())
  const name = request.name || contact.data?.crmName || contact.data?.fullName || t('stageFieldsDialog.fallbackContactName')
  const stageLabel = stage?.label ?? request.toStage

  const submit = () =>
    change.mutate(
      {
        contactId: request.contactId,
        toStage: request.toStage,
        fieldValues: fields.map((f) => ({ ref: f.ref, value: (values[f.ref] ?? '').trim() })).filter((v) => v.value),
      },
      {
        onSuccess: () => {
          toast.success(t('stageFieldsDialog.moveSuccess', { name, stage: stageLabel }))
          request.onDone?.()
          onClose()
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('stageFieldsDialog.dialogTitle', { name, stage: stageLabel })}</DialogTitle>
        <DialogDescription><Trans t={t} i18nKey="stageFieldsDialog.dialogDescription" components={{ b: <span className="text-destructive" /> }} /></DialogDescription>
      </DialogHeader>
      {!ready ? (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('stageFieldsDialog.loadingContact')}</p>
      ) : (
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.ref} className="space-y-1">
              <label htmlFor={`sf-${f.ref}`} className="text-sm">
                {f.label} {f.required ? <span className="text-destructive">*</span> : <span className="text-xs text-muted-foreground">{t('stageFieldsDialog.optional')}</span>}
              </label>
              {f.type === 'longtext' ? (
                <Textarea id={`sf-${f.ref}`} rows={3} value={values[f.ref] ?? ''} onChange={(e) => set(f.ref, e.target.value)} />
              ) : f.type === 'boolean' ? (
                <Select value={values[f.ref] || undefined} onValueChange={(v) => set(f.ref, v)}>
                  <SelectTrigger id={`sf-${f.ref}`}><SelectValue placeholder={t('stageFieldsDialog.choosePlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{t('stageFieldsDialog.yes')}</SelectItem>
                    <SelectItem value="false">{t('stageFieldsDialog.no')}</SelectItem>
                  </SelectContent>
                </Select>
              ) : f.type === 'select' ? (
                <Select value={values[f.ref] || undefined} onValueChange={(v) => set(f.ref, v)}>
                  <SelectTrigger id={`sf-${f.ref}`}><SelectValue placeholder={t('stageFieldsDialog.choosePlaceholder')} /></SelectTrigger>
                  <SelectContent>
                    {(f.options ?? []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`sf-${f.ref}`}
                  type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                  value={values[f.ref] ?? ''}
                  onChange={(e) => set(f.ref, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>{t('stageFieldsDialog.cancel')}</Button>
        <Button type="button" disabled={!ready || change.isPending || missing.length > 0} onClick={submit} title={missing.length ? t('stageFieldsDialog.missingFields', { fields: missing.map((m) => m.label).join(', ') }) : undefined}>
          {change.isPending && <Loader2 className="animate-spin" />} {t('stageFieldsDialog.submit')}
        </Button>
      </DialogFooter>
    </>
  )
}
